import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Button,
    Box,
    Link,
    Typography,
    CircularProgress,
    Alert,
    Backdrop,
    Collapse,
} from "@mui/material";
import TextInput from "qubCommon/Inputs/TextInput";
import DateInput from "qubCommon/Inputs/DateInput";
import { exportFiles, getPayment, savePayment } from "apis/controllers/clearings";
import { FormProvider, useForm } from "react-hook-form";
import UploadedFilesTable from "./UploadedFilesTable";
import AttachmentConfig from "./AttachmentConfig";
import { enqueueSnackbar } from "notistack";
import { safeDateFormat } from "qubCommon/helperMethods/dateHelper";
import { ArrowRight } from "@mui/icons-material";

// --- Payment Table ---
// --- Payment Table ---
const PreviewTablePayment = ({ rows = [] }) => {
    if (!rows.length) return null;

    return (
        <Box
            sx={{
                border: "1px solid #dcdcdc",
                borderRadius: 1.5,
                overflowX: "auto",
                mb: 2,
            }}
        >
            <Box sx={{ display: "table", width: "100%", minWidth: 800 }}>
                {/* Table header */}
                <Box
                    sx={{
                        display: "table-row",
                        backgroundColor: "grey.100",
                        borderBottom: "1px solid #dcdcdc",
                    }}
                >
                    {["Τύπος Αίτησης", "Πλήθος", "Ποσό (€)", "Κατάσταση", "Επόμενη Κατάσταση"].map(
                        (header, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "table-cell",
                                    fontWeight: 600,
                                    px: 2,
                                    py: 1,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {header}
                            </Box>
                        )
                    )}
                </Box>

                {/* Table rows */}
                {rows.map((row, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            display: "table-row",
                            backgroundColor: idx % 2 === 0 ? "background.paper" : "grey.50",
                            borderBottom:
                                idx < rows.length - 1 ? "1px solid #e0e0e0" : "none",
                        }}
                    >
                        <Box sx={{ display: "table-cell", px: 2, py: 1.2 }}>
                            <Typography fontWeight={600} color="primary.main">
                                {row.type}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "table-cell", px: 2, py: 1.2 }}>
                            {row.count}
                        </Box>
                        <Box sx={{ display: "table-cell", px: 2, py: 1.2 }}>
                            {row.amount?.toLocaleString("el-GR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }) || "—"}
                        </Box>
                        <Box sx={{ display: "table-cell", px: 2, py: 1.2 }}>
                            {row.currentStatus}
                        </Box>
                        <Box sx={{ display: "table-cell", px: 2, py: 1.2 }}>
                            {row.newStatus}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};


// --- Styled Info Display ---
const InfoBox = ({ label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography
            variant="h5"
            sx={{ fontWeight: 600, mr: 1, whiteSpace: "nowrap" }}
        >
            {label}:
        </Typography>
        <Typography variant="body2" color="text.primary">
            {value || "—"}
        </Typography>
    </Box>
);


// --- Main Dialog ---
const PaymentPreviewDialog = ({ open, onClose, row }) => {
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [openGroups, setOpenGroups] = useState({}); // track which groups are open
    const alertRef = useRef(null);

    const attachmentRefs = useRef([]);

    const methods = useForm();
    const fetchPayment = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPayment({ decisionId: row.id });

            if (response?.status === "success") {
                setPayment(response.data);
                methods.reset({
                    decisionDate: response.data.decisionDate || "",
                    decisionNumber: response.data.decisionNumber || "",
                    paymentDate: response.data.paymentDate || "",
                    lastPaymentDate: response.data.lastPaymentDate || "",
                    signedBy: response.data.signedBy || "",
                });
            } else {
                throw new Error(response?.message || "Αποτυχία φόρτωσης δεδομένων πληρωμής");
            }
        } catch (err) {
            console.error("Error fetching payment:", err);
            setError(err.message || "Αποτυχία φόρτωσης πληρωμής");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !row?.id) return;



        fetchPayment();
    }, [open, row?.id]);

    const handleExportFiles = async () => {

        try {
            setExporting(true);
            const res = await exportFiles({ decisionId: row?.id });
            if (res?.status === 'success') {
                fetchPayment();
            }
            else {
                setError(res?.message || "Αποτυχία εξαγωγής αρχείων");
                return;
            }

        } catch (err) {
            console.error("Error exporting file:", err);
            alert("Σφάλμα κατά τη λήψη του αρχείου");
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        if (submitError && alertRef.current) {
            alertRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [submitError]);


    const getAttachments = async () => {
        if (attachmentRefs.current) {
            await Promise.all(
                attachmentRefs.current.map(async (att) => {
                    if (
                        att &&
                        att.filebundles?.files
                            ?.filter((file) => file?.id)
                            ?.map((file) => file.id)?.length > 0
                    ) {
                        await att.uploadFiles();
                    }
                })
            );
        }
        const attachments = [];
        attachmentRefs.current.forEach((att) => {
            if (att) {
                // att.saveData();
                if (
                    att.filebundles?.files
                        ?.filter((file) => file?.id)
                        ?.map((file) => file.id)?.length > 0
                ) {



                    att.filebundles?.files
                        ?.filter((file) => file?.id)
                        ?.forEach((file) => {
                            console.log("file", file);
                            attachments.push({
                                Id: file.id, dataKey: att.filebundles.dataKey, dataValue: att.filebundles.dataValue
                            })
                        });
                }
                // attachments.push({
                //     dataKey: att.filebundles.dataKey,
                //     dataValue: att.filebundles.dataValue,
                //     bundles:
                //         att.filebundles?.files
                //             ?.filter((file) => file?.id)
                //             ?.map((file) => file.id) || [],
                // });
            }
        });

        return attachments;
    };

    const onSubmit = async (data) => {
        setSubmitError(null);
        setSubmitting(true);

        try {
            const attachments = await getAttachments();

            if (data.lastPaymentDate === "") data.lastPaymentDate = null
            const res = await savePayment({
                decisionId: row?.id,
                bundleIds: attachments,
                lastPaymentDate: data?.lastPaymentDate,
            });

            if (res?.status === "error") {
                setSubmitError(res?.message || "Αποτυχία ενημέρωσης πληρωμής");
            } else {
                if (res?.status === "success")
                    enqueueSnackbar("Η πληρωμή ενημερώθηκε με επιτυχία", { variant: "success" });
                onClose(); // success → close dialog
            }
        } catch (err) {
            console.error("Σφάλμα κατά την ενημέρωση:", err);
            setSubmitError(err.message || "Σφάλμα κατά την αποθήκευση");
        } finally {
            setSubmitting(false);
        }
    };


    const handleToggleGroup = (idx) => {
        setOpenGroups((prev) => ({
            ...prev,
            [idx]: !prev[idx], // toggle this group only
        }));
    };

    return (
        <FormProvider {...methods}>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { overflowY: "hidden" } }}>
                <DialogTitle variant="h4">Προβολή Πληρωμής</DialogTitle>

                <DialogContent dividers>
                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && payment && (
                        <>
                            {/* Info Section */}
                            <Grid container spacing={2} mt={1} mb={2}>
                                <Grid item xs={12} sm={6}>
                                    <InfoBox label="Αρ. Απόφασης" value={payment.decisionNumber} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InfoBox label="Ημερομηνία Απόφασης" value={safeDateFormat(payment.decisionDate)} />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <InfoBox label="Υπογράφων/ουσα Διατάκτης" value={payment.signedBy} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InfoBox label="Ημερομηνία Πληρωμής(Πίστωσης)" value={safeDateFormat(payment.paymentDate)} />
                                </Grid>


                                <Grid item xs={12} sm={6}>
                                    {payment.canEditLastPaymentDate ? (
                                        <DateInput
                                            name="lastPaymentDate"
                                            label="Ημερομηνία Τελευταίας Πληρωμής"
                                            isFullWidth
                                            defaultFromProps={false}
                                        />
                                    ) : null}
                                </Grid>
                            </Grid>

                            <PreviewTablePayment rows={payment.details || []} />

                            {payment.canExport &&
                                <Button variant='contained' color='primary' onClick={handleExportFiles}>Εξαγωγή Αρχείων</Button>
                            }

                            {/* 
                            <Button variant='contained' color='primary' sx={{ marginLeft: 1 }}>Ολοκλήρωση Καταβολής</Button> */}

                            {(payment.canImport && payment.attachments?.length > 0) && (
                                <Box sx={{ mt: 3 }}>

                                    {payment.attachments.map((attachmentGroup, idx) => (
                                        <Box key={idx} sx={{ mb: 3 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleToggleGroup(idx)}
                                                sx={{
                                                    mb: 1
                                                }}
                                                endIcon={
                                                    <ArrowRight
                                                        style={{
                                                            transition: "transform 0.3s ease",
                                                            transform: !!openGroups[idx] ? "rotate(90deg)" : "rotate(0deg)",
                                                        }}
                                                    />
                                                }
                                            >
                                                {attachmentGroup.title}
                                            </Button>
                                            <Collapse in={!!openGroups[idx]}>
                                                {attachmentGroup?.sections.map((section, sIdx) => (
                                                    <AttachmentConfig
                                                        key={(idx * 10) + sIdx}
                                                        id={row?.id}
                                                        dataKey={section.dataKey}
                                                        dataValue={section.dataValue}
                                                        ref={(el) => (attachmentRefs.current[(idx * 10) + sIdx] = el)}
                                                    />
                                                ))}
                                            </Collapse>

                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {payment.uploadedFiles?.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="h5" gutterBottom>
                                        Συνημμένα Αρχεία
                                    </Typography>
                                    <UploadedFilesTable
                                        uploadedFiles={payment.uploadedFiles}
                                        onDownload={(file) => console.log("Download:", file.fileName)}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                    {submitting && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {submitError && (
                        <Alert ref={alertRef} severity="error" sx={{ mt: 2 }}>
                            {submitError}
                        </Alert>
                    )}

                </DialogContent>

                <DialogActions sx={{ width: "100%", justifyContent: "space-between", px: 3, py: 2 }}>
                    <Button onClick={onClose} variant="contained" color="error">
                        Κλείσιμο
                    </Button>
                    <Button
                        onClick={methods.handleSubmit(onSubmit)}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                    >
                        {submitting ? "Αποθήκευση..." : "Ενημέρωση"}
                    </Button>
                </DialogActions>

                <Backdrop
                    open={exporting}
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Dialog>
        </FormProvider >
    );
};

export default PaymentPreviewDialog;
